/**
 * Visual effects: boost flame and trail, supersonic state, goal explosion,
 * tyre marks, impact flashes.
 *
 * DETERMINISM. Every random number in this file comes from a seeded PRNG.
 * `Math.random` is banned here for the same reason it is banned in the camera:
 * a replay that draws different sparks than the live play is not a replay, and
 * an effect that cannot be reproduced cannot be tested. The spawn helpers are
 * exported as pure functions that take an `Rng`, so "same seed, same particles"
 * is an assertion rather than a claim.
 *
 * FRAME-RATE INDEPENDENCE. All ramps use `1 - exp(-k * dt)` and all emission is
 * budgeted in particles per SECOND with a fractional carry, never "n particles
 * per frame". A per-frame emitter puts 2.4x as many particles on screen at
 * 144 Hz as at 60 Hz, and the boost trail is the first place that shows.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  ConeGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  NormalBlending,
  PerspectiveCamera,
  Points,
  Quaternion,
  RingGeometry,
  ShaderMaterial,
  SphereGeometry,
  Vector3,
} from 'three';
import { CAR_MAX_SPEED, SUPERSONIC_THRESHOLD } from '../physics/constants';
import { TEAM_COLORS, lerpColorHex, makeParticleTexture } from './materials';
import { FireworksShow } from './fireworks';

// ---------------------------------------------------------------------------
// Seeded PRNG
// ---------------------------------------------------------------------------

/**
 * mulberry32. Small, fast, and good enough for particle jitter; the only
 * property that matters here is that a given seed always replays the same
 * stream.
 */
export class Rng {
  private state: number;

  constructor(seed = 0x9e3779b9) {
    this.state = seed >>> 0;
  }

  /** Uniform in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform in [lo, hi). */
  range(lo: number, hi: number): number {
    return lo + (hi - lo) * this.next();
  }

  /** Uniform in [-1, 1). */
  signed(): number {
    return this.next() * 2 - 1;
  }

  /**
   * A direction uniform on the unit sphere. Rejection-free, so it consumes
   * exactly two draws per call and the stream stays predictable.
   */
  direction(out = new Vector3()): Vector3 {
    const z = this.signed();
    const a = this.next() * Math.PI * 2;
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    return out.set(r * Math.cos(a), r * Math.sin(a), z);
  }
}

/** Exponential smoothing factor for rate `k` over `dt`. Same law as the camera. */
export const rampFactor = (k: number, dt: number): number => 1 - Math.exp(-k * dt);

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const TUNING = {
  /** Boost flame ignition and extinction rates, 1/s. Ignition is snappier than
   *  extinction so the flame punches on and trails off. */
  flameIgnite: 22,
  flameExtinguish: 7,
  /** Boost trail: particles per second while boosting, and their lifetime. */
  trailRate: 220,
  trailLifeMin: 0.55,
  trailLifeMax: 1.15,
  trailSpeed: 260,
  trailSpread: 90,
  /** Supersonic streaks. */
  supersonicRate: 90,
  supersonicLife: 0.45,
  /** Goal explosion. */
  explosionCount: 1400,
  explosionSpeedMin: 900,
  explosionSpeedMax: 4200,
  explosionLifeMin: 0.7,
  explosionLifeMax: 2.1,
  shockwaveDuration: 1.1,
  shockwaveRadius: 3600,
  flashDecay: 2.6,
  /** The column of sparks the goal explosion throws straight up, which is what
   *  hands the moment over to the fireworks. */
  geyserCount: 140,
  geyserSpeedMin: 1600,
  geyserSpeedMax: 3400,
  /** Ball impact sparks. */
  impactCount: 90,
  impactSpeed: 1500,
  /**
   * Demolition debris. A fifth of a goal explosion's count, because a demo is
   * a moment rather than an event — and the pieces are large enough that a
   * goal-sized cloud of them would black out the pitch.
   */
  demoCount: 260,
  demoSpeed: 1300,
  /** Tyre marks. */
  markLife: 7,
  markWidth: 18,
  markMinSegment: 12,
  /** Particle drag, per second, applied exponentially. */
  drag: 1.6,
  /** Downward acceleration on debris particles, uu/s^2 in THREE's frame. */
  gravity: 640,
} as const;

// ---------------------------------------------------------------------------
// Particle pool
// ---------------------------------------------------------------------------

export interface ParticleSpawn {
  position: Vector3;
  velocity: Vector3;
  life: number;
  size: number;
  /** Size multiplier at death; > 1 puffs out, < 1 shrinks to nothing. */
  sizeGrowth: number;
  color: [number, number, number];
  /** Colour by normalized age, overriding `color` when present. */
  ramp?: (t: number) => [number, number, number];
  gravity: number;
  drag: number;
}

const PARTICLE_VERTEX_SHADER = `
attribute float aSize;
attribute float aAlpha;
attribute vec3 aColor;
uniform float uScale;
varying float vAlpha;
varying vec3 vColor;
void main() {
  vAlpha = aAlpha;
  vColor = aColor;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_PointSize = clamp(aSize * uScale / max(1.0, -mv.z), 0.0, 512.0);
  gl_Position = projectionMatrix * mv;
}`;

const PARTICLE_FRAGMENT_SHADER = `
uniform sampler2D uMap;
varying float vAlpha;
varying vec3 vColor;
void main() {
  float a = texture2D(uMap, gl_PointCoord).a * vAlpha;
  if (a <= 0.003) discard;
  gl_FragColor = vec4(vColor * a, a);
}`;

/**
 * A fixed-capacity additive particle pool. Allocation is a ring buffer: when it
 * is full the oldest particle is recycled rather than the spawn being dropped,
 * so a goal explosion always looks like a goal explosion even if the boost
 * trail happened to be busy.
 */
export class ParticlePool {
  readonly points: Points;

  private readonly capacity: number;
  private readonly position: Float32Array;
  private readonly color: Float32Array;
  private readonly size: Float32Array;
  private readonly alpha: Float32Array;

  private readonly velocity: Float32Array;
  private readonly life: Float32Array;
  private readonly maxLife: Float32Array;
  private readonly size0: Float32Array;
  private readonly size1: Float32Array;
  private readonly gravity: Float32Array;
  private readonly drag: Float32Array;
  private readonly ramps: (((t: number) => [number, number, number]) | null)[];

  private cursor = 0;
  private readonly material: ShaderMaterial;

  constructor(capacity = 6000) {
    this.capacity = capacity;
    this.position = new Float32Array(capacity * 3);
    this.color = new Float32Array(capacity * 3);
    this.size = new Float32Array(capacity);
    this.alpha = new Float32Array(capacity);
    this.velocity = new Float32Array(capacity * 3);
    this.life = new Float32Array(capacity);
    this.maxLife = new Float32Array(capacity);
    this.size0 = new Float32Array(capacity);
    this.size1 = new Float32Array(capacity);
    this.gravity = new Float32Array(capacity);
    this.drag = new Float32Array(capacity);
    this.ramps = new Array(capacity).fill(null);

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.position, 3));
    geometry.setAttribute('aColor', new BufferAttribute(this.color, 3));
    geometry.setAttribute('aSize', new BufferAttribute(this.size, 1));
    geometry.setAttribute('aAlpha', new BufferAttribute(this.alpha, 1));
    // Particles are placed in world space, so a bounding sphere computed once
    // would cull the whole system the moment the car left it.
    geometry.boundingSphere = null;

    this.material = new ShaderMaterial({
      uniforms: {
        uMap: { value: makeParticleTexture() },
        uScale: { value: 600 },
      },
      vertexShader: PARTICLE_VERTEX_SHADER,
      fragmentShader: PARTICLE_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
    });

    this.points = new Points(geometry, this.material);
    this.points.frustumCulled = false;
    this.points.name = 'particles';
  }

  get aliveCount(): number {
    let n = 0;
    for (let i = 0; i < this.capacity; i++) if (this.life[i] > 0) n++;
    return n;
  }

  spawn(p: ParticleSpawn): void {
    const i = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;

    this.position[i * 3] = p.position.x;
    this.position[i * 3 + 1] = p.position.y;
    this.position[i * 3 + 2] = p.position.z;
    this.velocity[i * 3] = p.velocity.x;
    this.velocity[i * 3 + 1] = p.velocity.y;
    this.velocity[i * 3 + 2] = p.velocity.z;
    this.life[i] = p.life;
    this.maxLife[i] = p.life;
    this.size0[i] = p.size;
    this.size1[i] = p.size * p.sizeGrowth;
    this.gravity[i] = p.gravity;
    this.drag[i] = p.drag;
    this.ramps[i] = p.ramp ?? null;
    this.color[i * 3] = p.color[0];
    this.color[i * 3 + 1] = p.color[1];
    this.color[i * 3 + 2] = p.color[2];
    this.size[i] = p.size;
    this.alpha[i] = 1;
  }

  update(dt: number): void {
    for (let i = 0; i < this.capacity; i++) {
      const life = this.life[i];
      if (life <= 0) continue;

      const remaining = life - dt;
      if (remaining <= 0) {
        this.life[i] = 0;
        this.alpha[i] = 0;
        this.size[i] = 0;
        continue;
      }
      this.life[i] = remaining;

      const decay = Math.exp(-this.drag[i] * dt);
      const o = i * 3;
      this.velocity[o] *= decay;
      this.velocity[o + 1] = this.velocity[o + 1] * decay - this.gravity[i] * dt;
      this.velocity[o + 2] *= decay;
      this.position[o] += this.velocity[o] * dt;
      this.position[o + 1] += this.velocity[o + 1] * dt;
      this.position[o + 2] += this.velocity[o + 2] * dt;

      const t = 1 - remaining / this.maxLife[i];
      this.size[i] = this.size0[i] + (this.size1[i] - this.size0[i]) * t;
      // Fade in over the first 10% and out over the rest: a particle that pops
      // into existence at full brightness reads as a glitch.
      this.alpha[i] = t < 0.1 ? t / 0.1 : 1 - (t - 0.1) / 0.9;

      const ramp = this.ramps[i];
      if (ramp) {
        const c = ramp(t);
        this.color[o] = c[0];
        this.color[o + 1] = c[1];
        this.color[o + 2] = c[2];
      }
    }

    const geometry = this.points.geometry;
    (geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (geometry.getAttribute('aColor') as BufferAttribute).needsUpdate = true;
    (geometry.getAttribute('aSize') as BufferAttribute).needsUpdate = true;
    (geometry.getAttribute('aAlpha') as BufferAttribute).needsUpdate = true;
  }

  /** Point size is in world units; the shader needs the pixels-per-uu factor. */
  setProjection(camera: PerspectiveCamera, viewportHeight: number): void {
    this.material.uniforms.uScale.value = camera.projectionMatrix.elements[5] * viewportHeight * 0.5;
  }

  dispose(): void {
    this.points.geometry.dispose();
    (this.material.uniforms.uMap.value as { dispose(): void }).dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Pure spawn generators
// ---------------------------------------------------------------------------

export interface ParticleInit {
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  life: number;
  size: number;
}

/**
 * Initial states of a goal explosion burst. Pure and seeded: given the same
 * `Rng` seed, origin and count, this returns byte-identical arrays every time,
 * which is what makes an explosion reproducible in a replay.
 *
 * The speed distribution is deliberately not uniform — `u^3` biases most of the
 * mass into a slow core with a few fast outliers, which is what makes a burst
 * look like an explosion rather than an expanding shell.
 */
export const explosionParticles = (
  rng: Rng,
  count: number,
  origin: { x: number; y: number; z: number },
  speedMin = TUNING.explosionSpeedMin,
  speedMax = TUNING.explosionSpeedMax
): ParticleInit[] => {
  const out: ParticleInit[] = [];
  const dir = new Vector3();
  for (let i = 0; i < count; i++) {
    rng.direction(dir);
    const u = rng.next();
    const speed = speedMin + (speedMax - speedMin) * u * u * u;
    out.push({
      px: origin.x + dir.x * 30,
      py: origin.y + dir.y * 30,
      pz: origin.z + dir.z * 30,
      vx: dir.x * speed,
      vy: dir.y * speed + 200,
      vz: dir.z * speed,
      life: TUNING.explosionLifeMin + rng.next() * (TUNING.explosionLifeMax - TUNING.explosionLifeMin),
      size: 14 + rng.next() * 34,
    });
  }
  return out;
};

/**
 * Initial states for one boost-trail particle. Emitted at the nozzle, pushed
 * backwards along `back` with a cone of jitter, and inheriting a fraction of the
 * car's own velocity so the trail hangs in the air where the car WAS instead of
 * being dragged along with it.
 */
export const trailParticle = (
  rng: Rng,
  nozzle: { x: number; y: number; z: number },
  back: { x: number; y: number; z: number },
  carVelocity: { x: number; y: number; z: number }
): ParticleInit => {
  const speed = TUNING.trailSpeed * (0.6 + rng.next() * 0.8);
  const spread = TUNING.trailSpread;
  return {
    px: nozzle.x + rng.signed() * 6,
    py: nozzle.y + rng.signed() * 6,
    pz: nozzle.z + rng.signed() * 6,
    vx: back.x * speed + rng.signed() * spread + carVelocity.x * 0.08,
    vy: back.y * speed + rng.signed() * spread + carVelocity.y * 0.08 + 40,
    vz: back.z * speed + rng.signed() * spread + carVelocity.z * 0.08,
    life: TUNING.trailLifeMin + rng.next() * (TUNING.trailLifeMax - TUNING.trailLifeMin),
    size: 22 + rng.next() * 20,
  };
};

// ---------------------------------------------------------------------------
// Tyre marks
// ---------------------------------------------------------------------------

/**
 * Tyre marks, as a ring buffer of quads laid flat on the pitch. Each quad is a
 * segment between two successive wheel samples, so a skid reads as a continuous
 * ribbon; alpha lives in a 4-component vertex colour and decays over
 * TUNING.markLife.
 */
export class TyreMarks {
  readonly mesh: Mesh;

  private readonly capacity: number;
  private readonly colors: Float32Array;
  private readonly positions: Float32Array;
  private readonly age: Float32Array;
  private readonly strength: Float32Array;
  private cursor = 0;
  private readonly material: MeshBasicMaterial;

  constructor(capacity = 512) {
    this.capacity = capacity;
    this.positions = new Float32Array(capacity * 4 * 3);
    this.colors = new Float32Array(capacity * 4 * 4);
    this.age = new Float32Array(capacity).fill(Infinity);
    this.strength = new Float32Array(capacity);

    const indices = new Uint16Array(capacity * 6);
    for (let q = 0; q < capacity; q++) {
      const v = q * 4;
      const o = q * 6;
      indices[o] = v;
      indices[o + 1] = v + 1;
      indices[o + 2] = v + 2;
      indices[o + 3] = v;
      indices[o + 4] = v + 2;
      indices[o + 5] = v + 3;
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    geometry.setAttribute('color', new BufferAttribute(this.colors, 4));
    geometry.setIndex(new BufferAttribute(indices, 1));
    geometry.boundingSphere = null;

    this.material = new MeshBasicMaterial({
      color: 0x000000,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: NormalBlending,
    });

    this.mesh = new Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 2;
    this.mesh.name = 'tyre-marks';
  }

  /**
   * Lay a segment from `from` to `to`, both on the floor (THREE frame, so the
   * floor is the XZ plane at y = 0). `strength` in [0, 1] is how black it is.
   */
  addSegment(from: Vector3, to: Vector3, strength: number): void {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-3) return;
    // Perpendicular in the floor plane.
    const nx = (-dz / len) * (TUNING.markWidth / 2);
    const nz = (dx / len) * (TUNING.markWidth / 2);

    const q = this.cursor;
    this.cursor = (this.cursor + 1) % this.capacity;
    const o = q * 12;
    const y = 1.5; // a hair above the pitch, to stay out of the z-fight
    this.positions[o] = from.x - nx; this.positions[o + 1] = y; this.positions[o + 2] = from.z - nz;
    this.positions[o + 3] = from.x + nx; this.positions[o + 4] = y; this.positions[o + 5] = from.z + nz;
    this.positions[o + 6] = to.x + nx; this.positions[o + 7] = y; this.positions[o + 8] = to.z + nz;
    this.positions[o + 9] = to.x - nx; this.positions[o + 10] = y; this.positions[o + 11] = to.z - nz;

    this.age[q] = 0;
    this.strength[q] = Math.max(0, Math.min(1, strength));
  }

  update(dt: number): void {
    for (let q = 0; q < this.capacity; q++) {
      const age = this.age[q];
      if (!Number.isFinite(age)) continue;
      const next = age + dt;
      this.age[q] = next;
      const alpha = next >= TUNING.markLife ? 0 : (1 - next / TUNING.markLife) * this.strength[q] * 0.7;
      for (let v = 0; v < 4; v++) {
        const o = (q * 4 + v) * 4;
        this.colors[o] = 0;
        this.colors[o + 1] = 0;
        this.colors[o + 2] = 0;
        this.colors[o + 3] = alpha;
      }
    }
    (this.mesh.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.mesh.geometry.getAttribute('color') as BufferAttribute).needsUpdate = true;
  }

  clear(): void {
    this.age.fill(Infinity);
    this.colors.fill(0);
    (this.mesh.geometry.getAttribute('color') as BufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Per-car effect state
// ---------------------------------------------------------------------------

export interface CarEffectState {
  /** Interpolated world transform, THREE frame. */
  position: Vector3;
  quaternion: Quaternion;
  velocity: Vector3;
  /** Nozzle position in world space. */
  nozzle: Vector3;
  boosting: boolean;
  supersonic: boolean;
  onGround: boolean;
  /** 0..1 analog handbrake, for the tyre marks. */
  powerslide: number;
  speed: number;
  demolished: boolean;
}

/**
 * The equipped boost and trail, as this system needs them.
 *
 * Declared HERE rather than imported from ./livery so the effects system keeps
 * no dependency on the cosmetics layer: a boost is a colour, a rate and a
 * gravity, and a system that knows what a `Loadout` is would be a system a test
 * could not drive with three numbers. ./livery produces these; `setCarStyle`
 * consumes them; neither knows about the other's tables.
 */
export interface CarEffectStyle {
  /** Flame cone colour, cruising and supersonic. */
  core: number;
  supersonicCore: number;
  /** Boost trail particle colour at birth and at death. */
  hot: number;
  cool: number;
  /** Flame cone scale, along the car and across it. */
  length: number;
  width: number;
  /** Flicker rate, rad/s. */
  flicker: number;
  /** Multipliers on the stock boost trail. */
  rate: number;
  size: number;
  growth: number;
  life: number;
  /** uu/s^2 on boost trail particles. Negative rises. */
  buoyancy: number;
  /** Supersonic streak colour and multipliers. */
  trailHot: number;
  trailCool: number;
  trailRate: number;
  trailLife: number;
  trailSize: number;
}

/** The stock flame and streak — what a car with no loadout flies. */
export const STOCK_EFFECT_STYLE: CarEffectStyle = Object.freeze({
  core: 0xffb04a,
  supersonicCore: 0x9fd8ff,
  hot: 0xfff6dd,
  cool: 0xd63a12,
  length: 1,
  width: 1,
  flicker: 47,
  rate: 1,
  size: 1,
  growth: 2.4,
  life: 1,
  buoyancy: -90,
  trailHot: 0xeaf4ff,
  trailCool: 0x2f6bd4,
  trailRate: 1,
  trailLife: 1,
  trailSize: 1,
});

/**
 * A two-stop colour ramp over particle age, as the pool's `ramp` wants it.
 *
 * Built once per style rather than per particle: the pool stores the function
 * pointer and calls it every frame for every live particle, so a closure
 * allocated at spawn time would be one allocation per particle per boost.
 */
const rampBetween = (hot: number, cool: number): ((t: number) => [number, number, number]) => {
  const mid = lerpColorHex(hot, cool, 0.35);
  return (t: number): [number, number, number] => {
    const k = t < 0 ? 0 : t > 1 ? 1 : t;
    const hex = k < 0.35 ? lerpColorHex(hot, mid, k / 0.35) : lerpColorHex(mid, cool, (k - 0.35) / 0.65);
    return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
  };
};

interface CarEffects {
  flame: Mesh;
  flameMaterial: MeshBasicMaterial;
  intensity: number;
  trailCarry: number;
  supersonicCarry: number;
  lastMark: Vector3 | null;
  style: CarEffectStyle;
  /** Cached ramps, so a colour change costs one closure and not one per spawn. */
  boostRamp: (t: number) => [number, number, number];
  trailRamp: (t: number) => [number, number, number];
}

// ---------------------------------------------------------------------------
// The system
// ---------------------------------------------------------------------------

export interface EffectsOptions {
  seed?: number;
  particleCapacity?: number;
  carCount?: number;
}

export class EffectsSystem {
  readonly group = new Group();
  readonly particles: ParticlePool;
  readonly tyreMarks = new TyreMarks();
  /** The goal display. Fires from `goalExplosion` and burns on its own clock. */
  readonly fireworks: FireworksShow;

  /** 0..1 white-out for the HUD to render. Decays on its own. */
  screenFlash = 0;
  /** 0..1 speed-line intensity for the HUD, from the followed car's speed. */
  speedIntensity = 0;

  private readonly rng: Rng;
  /**
   * Seed for the fireworks, advanced per goal.
   *
   * The display gets its OWN generator rather than drawing from `this.rng`,
   * because `this.rng` has by then been consumed an unpredictable number of
   * times by whatever boosting and skidding the match happened to contain. A
   * display that depends on how much the players boosted is not reproducible in
   * any useful sense; one keyed on the goal number is.
   */
  private goalCount = 0;
  private readonly fireworksSeed: number;
  private readonly cars: CarEffects[] = [];
  private readonly shockwaves: { mesh: Mesh; material: MeshBasicMaterial; t: number }[] = [];
  private readonly flashes: { mesh: Mesh; material: MeshBasicMaterial; t: number; life: number }[] = [];
  private readonly scratchA = new Vector3();
  private readonly scratchB = new Vector3();

  constructor(options: EffectsOptions = {}) {
    const seed = options.seed ?? 0x5f3759df;
    this.rng = new Rng(seed);
    this.fireworksSeed = (seed ^ 0x9e3779b9) >>> 0;
    this.particles = new ParticlePool(options.particleCapacity ?? 6000);
    this.fireworks = new FireworksShow(this.particles);
    this.group.name = 'effects';
    this.group.add(this.particles.points, this.tyreMarks.mesh, this.fireworks.group);

    for (let i = 0; i < (options.carCount ?? 2); i++) this.cars.push(this.makeCarEffects());
  }

  private makeCarEffects(): CarEffects {
    const flameMaterial = new MeshBasicMaterial({
      color: STOCK_EFFECT_STYLE.core,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    // A cone pointing along +Z, i.e. out of the back of the car.
    const flame = new Mesh(new ConeGeometry(16, 90, 12, 1, true), flameMaterial);
    flame.geometry.rotateX(Math.PI / 2);
    flame.geometry.translate(0, 0, 45);
    flame.visible = false;
    flame.frustumCulled = false;
    this.group.add(flame);
    return {
      flame,
      flameMaterial,
      intensity: 0,
      trailCarry: 0,
      supersonicCarry: 0,
      lastMark: null,
      style: STOCK_EFFECT_STYLE,
      boostRamp: rampBetween(STOCK_EFFECT_STYLE.hot, STOCK_EFFECT_STYLE.cool),
      trailRamp: rampBetween(STOCK_EFFECT_STYLE.trailHot, STOCK_EFFECT_STYLE.trailCool),
    };
  }

  /**
   * Give one car its equipped boost and trail.
   *
   * Called once when the car is built, never per frame: the style is a property
   * of the loadout and the loadout does not change during a match. The two
   * colour ramps are rebuilt here for the same reason — once per equip is free,
   * once per particle is not.
   */
  setCarStyle(index: number, style: CarEffectStyle): void {
    const fx = this.cars[index];
    if (!fx) return;
    fx.style = style;
    fx.boostRamp = rampBetween(style.hot, style.cool);
    fx.trailRamp = rampBetween(style.trailHot, style.trailCool);
    // The cone is scaled every frame from `intensity`, so the STYLE's own scale
    // goes on the geometry-side multiplier rather than on the material.
    fx.flameMaterial.color.setHex(style.core);
  }

  /**
   * Advance one car's effects. Called per rendered frame with the INTERPOLATED
   * transform, never the raw physics state — a boost flame that snaps between
   * 120 Hz samples is exactly as ugly as a car that does.
   */
  updateCar(index: number, s: CarEffectState, dt: number): void {
    const fx = this.cars[index];
    if (!fx) return;

    const style = fx.style;
    const target = s.boosting && !s.demolished ? 1 : 0;
    const rate = target > fx.intensity ? TUNING.flameIgnite : TUNING.flameExtinguish;
    fx.intensity += (target - fx.intensity) * rampFactor(rate, dt);
    if (fx.intensity < 1e-3) fx.intensity = 0;

    // The flame sits at the nozzle and points back along the car's +Z.
    fx.flame.visible = fx.intensity > 0.02;
    if (fx.flame.visible) {
      fx.flame.position.copy(s.nozzle);
      fx.flame.quaternion.copy(s.quaternion);
      // Flicker is a function of accumulated time, not of the frame counter, so
      // it looks identical at any refresh rate. The RATE is the item's — a
      // plasma jet is steadier than a shower of sparks, and that is most of what
      // makes the two read as different things.
      const flicker = 0.85 + 0.15 * Math.sin(performanceSeconds() * style.flicker);
      const k = fx.intensity * flicker;
      fx.flame.scale.set(k * style.width, k * style.width, k * style.length);
      fx.flameMaterial.opacity = 0.35 + 0.55 * fx.intensity;
      fx.flameMaterial.color.setHex(s.supersonic ? style.supersonicCore : style.core);
    }

    // Backwards direction in world space: the car's local +Z.
    const back = this.scratchA.set(0, 0, 1).applyQuaternion(s.quaternion);

    if (fx.intensity > 0.05 && !s.demolished) {
      fx.trailCarry += TUNING.trailRate * style.rate * fx.intensity * dt;
      while (fx.trailCarry >= 1) {
        fx.trailCarry -= 1;
        const p = trailParticle(this.rng, s.nozzle, back, s.velocity);
        this.particles.spawn({
          position: this.scratchB.set(p.px, p.py, p.pz),
          velocity: new Vector3(p.vx, p.vy, p.vz),
          life: p.life * style.life,
          size: p.size * style.size,
          sizeGrowth: style.growth,
          color: fx.boostRamp(0),
          ramp: fx.boostRamp,
          gravity: style.buoyancy,
          drag: 2.2,
        });
      }
    }

    if (s.supersonic && !s.demolished) {
      fx.supersonicCarry += TUNING.supersonicRate * style.trailRate * dt;
      while (fx.supersonicCarry >= 1) {
        fx.supersonicCarry -= 1;
        const d = this.rng.direction(new Vector3()).multiplyScalar(40);
        this.particles.spawn({
          position: new Vector3(s.position.x + d.x, s.position.y + d.y * 0.5 + 20, s.position.z + d.z),
          velocity: new Vector3(
            -s.velocity.x * 0.25 + this.rng.signed() * 60,
            -s.velocity.y * 0.25 + this.rng.signed() * 60,
            -s.velocity.z * 0.25 + this.rng.signed() * 60
          ),
          life: TUNING.supersonicLife * style.trailLife,
          size: (10 + this.rng.next() * 12) * style.trailSize,
          sizeGrowth: 0.2,
          color: fx.trailRamp(0),
          ramp: fx.trailRamp,
          gravity: 0,
          drag: 1.2,
        });
      }
    } else {
      fx.supersonicCarry = 0;
    }

    // Tyre marks: only while sliding hard on the ground. The test is lateral
    // slip OR the handbrake, so a clean fast turn does not smear the pitch.
    const sliding =
      s.onGround && !s.demolished && (s.powerslide > 0.25 || this.lateralSlip(s) > 0.35);
    if (sliding && s.speed > 300) {
      const contact = this.scratchB.set(s.position.x, 0, s.position.z);
      if (fx.lastMark) {
        if (fx.lastMark.distanceTo(contact) >= TUNING.markMinSegment) {
          this.tyreMarks.addSegment(fx.lastMark, contact, Math.min(1, s.speed / 1400));
          fx.lastMark.copy(contact);
        }
      } else {
        fx.lastMark = contact.clone();
      }
    } else {
      fx.lastMark = null;
    }
  }

  /** How sideways the car is travelling, 0 = straight, 1 = fully broadside. */
  private lateralSlip(s: CarEffectState): number {
    if (s.speed < 1e-3) return 0;
    const forward = this.scratchA.set(0, 0, -1).applyQuaternion(s.quaternion);
    const along = Math.abs(forward.dot(s.velocity)) / s.speed;
    return 1 - Math.min(1, along);
  }

  /** Ball hit: a bright pop plus a spray of sparks. `strength` is uu/s of impact. */
  ballImpact(position: Vector3, strength: number): void {
    const power = Math.min(1, strength / 3000);
    if (power < 0.12) return;

    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const mesh = new Mesh(new SphereGeometry(40 + 90 * power, 12, 8), material);
    mesh.position.copy(position);
    this.group.add(mesh);
    this.flashes.push({ mesh, material, t: 0, life: 0.22 + 0.2 * power });

    const count = Math.round(TUNING.impactCount * power);
    const dir = new Vector3();
    for (let i = 0; i < count; i++) {
      this.rng.direction(dir);
      const speed = TUNING.impactSpeed * power * (0.3 + this.rng.next());
      this.particles.spawn({
        position: new Vector3(position.x + dir.x * 40, position.y + dir.y * 40, position.z + dir.z * 40),
        velocity: new Vector3(dir.x * speed, dir.y * speed + 120, dir.z * speed),
        life: 0.25 + this.rng.next() * 0.5,
        size: 8 + this.rng.next() * 14,
        sizeGrowth: 0.4,
        color: [1, 0.92, 0.72],
        gravity: TUNING.gravity,
        drag: 1.8,
      });
    }
  }

  /**
   * The goal moment: the ball detonates, throws a column of sparks straight up,
   * pushes a shockwave ring out along the pitch, whites the screen out — and
   * that column is what lights the fireworks.
   *
   * The order matters to the read. The explosion is the CAUSE; the display is
   * the consequence, and `TUNING.leadIn` in ./fireworks is the beat between
   * them. Fired together they look like two unrelated effects that happened to
   * start on the same frame.
   *
   * `goalSign` is the PHYSICS y sign of the net the ball went into, so the
   * shells stand over the right half of the pitch.
   */
  goalExplosion(position: Vector3, team: 0 | 1, goalSign = 1): void {
    const color = new Color(TEAM_COLORS[team]);
    const tint: [number, number, number] = [color.r, color.g, color.b];

    for (const p of explosionParticles(this.rng, TUNING.explosionCount, position)) {
      // Two thirds team-coloured, one third white-hot, so the burst has a core.
      const hot = this.rng.next() < 0.34;
      this.particles.spawn({
        position: new Vector3(p.px, p.py, p.pz),
        velocity: new Vector3(p.vx, p.vy, p.vz),
        life: p.life,
        size: p.size,
        sizeGrowth: 1.6,
        color: hot ? [1, 0.95, 0.85] : tint,
        gravity: TUNING.gravity * 0.5,
        drag: 0.9,
      });
    }

    const material = new MeshBasicMaterial({
      color: TEAM_COLORS[team],
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide,
    });
    const ring = new Mesh(new RingGeometry(0.86, 1, 96), material);
    ring.position.copy(position);
    ring.rotation.x = -Math.PI / 2;
    this.group.add(ring);
    this.shockwaves.push({ mesh: ring, material, t: 0 });

    // The geyser: a narrow, fast, upward cone out of the ball. Narrow on
    // purpose — a wide one is just more explosion, and the point of this is to
    // draw the eye up to where the shells are about to be.
    for (let i = 0; i < TUNING.geyserCount; i++) {
      const a = this.rng.next() * Math.PI * 2;
      const r = this.rng.next() * 0.28;
      const speed = TUNING.geyserSpeedMin + this.rng.next() * (TUNING.geyserSpeedMax - TUNING.geyserSpeedMin);
      this.particles.spawn({
        position: new Vector3(position.x, position.y, position.z),
        velocity: new Vector3(Math.cos(a) * r * speed, speed, Math.sin(a) * r * speed),
        life: 0.9 + this.rng.next() * 1.1,
        size: 10 + this.rng.next() * 18,
        sizeGrowth: 0.5,
        color: this.rng.next() < 0.4 ? [1, 0.95, 0.82] : tint,
        gravity: TUNING.gravity * 0.7,
        drag: 0.7,
      });
    }

    this.screenFlash = 1;

    // Seeded per GOAL, not per match: goal number 3 of a match always puts on
    // the same display, whatever happened in the two before it.
    this.fireworks.start(
      new Rng((this.fireworksSeed + Math.imul(this.goalCount++, 0x9e3779b1)) >>> 0),
      team,
      goalSign
    );
  }

  /**
   * A car being wrecked.
   *
   * Deliberately NOT a small goal explosion: a demo is violent but it is not
   * an event the whole stadium stops for, so there is no shockwave ring, no
   * screen flash and no geyser drawing the eye upward. What it does have is
   * DEBRIS — heavier, slower, gravity-bound chunks that arc and fall, rather
   * than the weightless sparks an impact throws. That difference is the read:
   * something broke, and the pieces went everywhere.
   *
   * Tinted by the wrecked car's team so a glance says whose car just left.
   */
  demolition(position: Vector3, team: 0 | 1): void {
    const color = new Color(TEAM_COLORS[team]);
    const tint: [number, number, number] = [color.r, color.g, color.b];

    // A brief hot core, so the burst has an origin rather than being a cloud
    // that appears. Short-lived: the debris is the part worth watching.
    const material = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: AdditiveBlending,
    });
    const flash = new Mesh(new SphereGeometry(96, 14, 10), material);
    flash.position.copy(position);
    this.group.add(flash);
    this.flashes.push({ mesh: flash, material, t: 0, life: 0.26 });

    const dir = new Vector3();
    for (let i = 0; i < TUNING.demoCount; i++) {
      this.rng.direction(dir);
      // Biased upward and outward: a car that is hit hard enough to explode
      // does not spray its parts into the floor.
      const speed = TUNING.demoSpeed * (0.35 + this.rng.next() * 0.9);
      this.particles.spawn({
        position: new Vector3(
          position.x + dir.x * 28,
          position.y + dir.y * 28 + 20,
          position.z + dir.z * 28
        ),
        velocity: new Vector3(
          dir.x * speed,
          Math.abs(dir.y) * speed * 0.8 + 260,
          dir.z * speed
        ),
        life: 0.7 + this.rng.next() * 0.9,
        // Chunks, not sparks: bigger, and they SHRINK rather than bloom.
        size: 12 + this.rng.next() * 22,
        sizeGrowth: -0.35,
        // A third white-hot, the rest the car's colour — the same core-and-body
        // split the goal explosion uses, at a quieter ratio.
        color: this.rng.next() < 0.3 ? [1, 0.93, 0.8] : tint,
        gravity: TUNING.gravity * 1.6,
        drag: 0.5,
      });
    }
  }

  /** Advance everything that is not per-car. */
  update(dt: number, camera: PerspectiveCamera, viewportHeight: number, followSpeed = 0): void {
    this.particles.setProjection(camera, viewportHeight);
    // Before the pool, so a burst spawned this frame is integrated this frame
    // rather than sitting motionless for one.
    this.fireworks.update(dt);
    this.particles.update(dt);
    this.tyreMarks.update(dt);

    this.screenFlash = Math.max(0, this.screenFlash - TUNING.flashDecay * dt * this.screenFlash - dt * 0.15);

    // Speed lines ramp in over the top third of the speed range and are already
    // strong by the time the car is supersonic.
    const t = (followSpeed - SUPERSONIC_THRESHOLD * 0.55) / (CAR_MAX_SPEED - SUPERSONIC_THRESHOLD * 0.55);
    this.speedIntensity = Math.max(0, Math.min(1, t));

    for (let i = this.shockwaves.length - 1; i >= 0; i--) {
      const s = this.shockwaves[i];
      s.t += dt;
      const k = s.t / TUNING.shockwaveDuration;
      if (k >= 1) {
        this.group.remove(s.mesh);
        s.mesh.geometry.dispose();
        s.material.dispose();
        this.shockwaves.splice(i, 1);
        continue;
      }
      // Ease-out expansion: fast at the front, so it reads as a pressure wave.
      const radius = TUNING.shockwaveRadius * (1 - Math.pow(1 - k, 2.2));
      s.mesh.scale.setScalar(Math.max(1e-3, radius));
      s.material.opacity = 0.85 * Math.pow(1 - k, 1.6);
    }

    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i];
      f.t += dt;
      const k = f.t / f.life;
      if (k >= 1) {
        this.group.remove(f.mesh);
        f.mesh.geometry.dispose();
        f.material.dispose();
        this.flashes.splice(i, 1);
        continue;
      }
      f.mesh.scale.setScalar(1 + k * 1.8);
      f.material.opacity = 0.9 * (1 - k) * (1 - k);
    }
  }

  /** Wipe transient state — used on kickoff so the pitch starts clean. */
  reset(): void {
    this.tyreMarks.clear();
    this.fireworks.reset();
    for (const c of this.cars) {
      c.intensity = 0;
      c.lastMark = null;
      c.flame.visible = false;
    }
    this.screenFlash = 0;
  }

  dispose(): void {
    this.particles.dispose();
    this.tyreMarks.dispose();
    this.fireworks.dispose();
    for (const c of this.cars) {
      c.flame.geometry.dispose();
      c.flameMaterial.dispose();
    }
    for (const s of this.shockwaves) {
      s.mesh.geometry.dispose();
      s.material.dispose();
    }
    for (const f of this.flashes) {
      f.mesh.geometry.dispose();
      f.material.dispose();
    }
  }
}

/**
 * Wall-clock seconds, used only for flame flicker — a purely cosmetic phase
 * that never feeds anything deterministic. Falls back to 0 where `performance`
 * does not exist (Node), which keeps this module importable in tests.
 */
const performanceSeconds = (): number =>
  typeof performance === 'undefined' ? 0 : performance.now() / 1000;
